import { DRAG_LINK, DRAG_MOVE, DRAG_RESIZE_END, DRAG_RESIZE_START, TASK_MIN_WIDTH } from "../../helpers/Const";
import { LINKER_DISTANCE, LINKER_DIAMETER } from "../viewport/DataTask";
import React, { PureComponent } from "react";

import Link, { IPos } from "../../components/links/Link";
import { differenceInMinutes, startOfDay } from "date-fns";
import styled from "styled-components/macro";
import { connect } from "react-redux";
import { snapMouseMoveBasedOnZoomMode } from "src/gantt/helpers/MiscUtils";
import { ensureStartAndEnd } from "src/backend/helpers/dateHelpers";
import { iRootState } from "src/rematch/store";
import { IHierarchyItem } from "src/backend/interfaces/IItem";
import { IObjectTypeConfigurationItem } from "src/backend/interfaces/configuration/IConfiguration";

interface ILinkViewPortRootProps {
  width: number;
  height: number;
}

const LinkViewPortRoot = styled.svg`
  width: ${(props: ILinkViewPortRootProps) => props.width}px;
  height: ${(props: ILinkViewPortRootProps) => props.height}px;
  position: absolute;
  z-index: 10;
  pointer-events: none;
`;

interface ILinkViewPortProps {
  taskHeight: number;
  scrollLeft: number;
  scrollTop: number;
  startDate: Date;
  hourWidth: number;
  visibleData: IHierarchyItem[];
  allData: IHierarchyItem[];
  dragTaskId: number;
  dragType: string;
  xDelta: number;
  yDelta: number;
  zoomMode: string;
  dragUnderway: boolean;
  chartTotalWidth: number;
  getObjectTypeConfig?: (objectTypeId: number) => IObjectTypeConfigurationItem,
}

interface ILinkViewPortState {
  selectedLink: { startTaskId: number, endTaskId: number };
}

class LinkViewPort extends PureComponent<ILinkViewPortProps, ILinkViewPortState> {

  idCounter: number;

  constructor(props: ILinkViewPortProps) {
    super(props);
    this.state = {
      selectedLink: { startTaskId: null, endTaskId: null },
    };
  }

  // TODO: Potential (smallish?) optimisation: turn component from PureComponent to a regular Component and skip render
  // if scrollLeft or scrollTop have changed in an unmeaningful way.
  componentDidUpdate(prevProps: ILinkViewPortProps) {
    if (prevProps.scrollLeft !== this.props.scrollLeft || prevProps.scrollTop !== this.props.scrollTop) {
      this.setState(() => ({ selectedLink: { startTaskId: null, endTaskId: null } }));
    }
  }

  selectLink = (startTaskId: number, endTaskId: number) => {
    this.setState(() => ({ selectedLink: { startTaskId: startTaskId, endTaskId: endTaskId } }));
  };

  getItemPosition = (index: number, date: Date): IPos => {
    const rowIndex = this.getRowIndex(index);
    const x = Math.round(differenceInMinutes(date, this.props.startDate) / 60 * this.props.hourWidth);
    const y = rowIndex * this.props.taskHeight + this.props.taskHeight / 2;
    return { x: x, y: y };
  };

  renderLink(startPosition: IPos, endPosition: IPos, startTask: IHierarchyItem, endTask: IHierarchyItem) {
    const isSelected = this.state.selectedLink.startTaskId === startTask.id && this.state.selectedLink.endTaskId === endTask.id;
    return (
      <Link
        key={this.idCounter++}
        startPos={startPosition}
        endPos={endPosition}
        startTask={startTask}
        endTask={endTask}
        isSelected={isSelected}
        selectLink={this.selectLink}
      />
    );
  }

  renderLinks = () => {
    const links = [];
    for (const startTask of this.props.visibleData) {
      const startTaskobjectTypeConfig = this.props.getObjectTypeConfig(startTask.objectType);
      const [validStartTaskStart, validStartTaskEnd] = ensureStartAndEnd(startTask.start, startTask.end, startOfDay(Date.now()), startTask.duration, startTaskobjectTypeConfig.durationUnit);
      for (const linkedTaskId of startTask.linksTo) {
        const endTask = this.props.allData[linkedTaskId];
        const endTaskobjectTypeConfig = this.props.getObjectTypeConfig(endTask.objectType);
        const [validEndTaskStart, _validEndTaskEnd] = ensureStartAndEnd(endTask.start, endTask.end, startOfDay(Date.now()), endTask.duration, endTaskobjectTypeConfig.durationUnit);
        const startPosition = this.getItemPosition(startTask.id, validStartTaskEnd);

        const taskWidth = Math.round(differenceInMinutes(validStartTaskEnd, validStartTaskStart) / 60 * this.props.hourWidth);
        const minWidthAdjustment = Math.max(TASK_MIN_WIDTH - taskWidth, 0);
        const startTaskBeingRescheduled = this.props.dragTaskId === startTask.id;

        if (startTaskBeingRescheduled) {
          if (this.props.dragType === DRAG_RESIZE_END) {
            const snappedMoveDeltaInPixels = snapMouseMoveBasedOnZoomMode(
              this.props.xDelta + minWidthAdjustment,
              this.props.zoomMode,
              this.props.hourWidth
            );
            startPosition.x += snappedMoveDeltaInPixels;
          } else if (this.props.dragType === DRAG_MOVE) {
            const snappedMoveDeltaInPixels = snapMouseMoveBasedOnZoomMode(this.props.xDelta, this.props.zoomMode, this.props.hourWidth);
            startPosition.x += snappedMoveDeltaInPixels + minWidthAdjustment;
          }
        } else {
          startPosition.x += minWidthAdjustment;
        }

        const endPosition = this.getItemPosition(endTask.id, validEndTaskStart);
        const endTaskBeingRescheduled = this.props.dragTaskId === endTask.id;
        if (endTaskBeingRescheduled && (this.props.dragType === DRAG_MOVE || this.props.dragType === DRAG_RESIZE_START)) {
          const snappedMoveDeltaInPixels = snapMouseMoveBasedOnZoomMode(this.props.xDelta, this.props.zoomMode, this.props.hourWidth);
          endPosition.x += snappedMoveDeltaInPixels;
        }

        links.push(this.renderLink(startPosition, endPosition, startTask, endTask));
      }
    }
    if (this.props.dragType === DRAG_LINK && this.props.dragTaskId !== null && this.props.dragUnderway) {
      const startTask = this.props.allData[this.props.dragTaskId];
      const startTaskobjectTypeConfig = this.props.getObjectTypeConfig(startTask.objectType);
      const [validStartTaskStart, validStartTaskEnd] = ensureStartAndEnd(startTask.start, startTask.end, startOfDay(Date.now()), startTask.duration, startTaskobjectTypeConfig.durationUnit);
      const startPosition = this.getItemPosition(startTask.id, validStartTaskStart);
      const taskWidth = Math.round(differenceInMinutes(validStartTaskEnd, validStartTaskStart) / 60 * this.props.hourWidth);
      const minWidthAdjustment = Math.max(TASK_MIN_WIDTH - taskWidth, 0);
      startPosition.x += taskWidth + minWidthAdjustment + LINKER_DISTANCE + LINKER_DIAMETER / 2;
      const endPosition = {
        x: startPosition.x + this.props.xDelta,
        y: startPosition.y + this.props.yDelta,
      };
      links.push(<Link drawStraight={true} key={-1} startPos={startPosition} endPos={endPosition} startTask={startTask} />);
    }
    return links;
  };

  getRowIndex = (id: number) => {
    //TODO: Instead of find index, group data by row number.
    return this.props.visibleData.findIndex((rowItem) => {
      return id === rowItem.id;
    });
  };

  render() {
    this.idCounter = 0;
    return (
      <LinkViewPortRoot width={this.props.chartTotalWidth} height={this.props.taskHeight * this.props.visibleData.length}>
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" strokeLinejoin="round" />
          </marker>
        </defs>
        <g>{this.renderLinks()}</g>
      </LinkViewPortRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  taskHeight: state.UI.displaySettings.taskHeight,
  getObjectTypeConfig: (objectTypeId: number) => Array.from(state.common.configuration.objectTypeConfigurations.values()).find(i => i.objectTypeId === objectTypeId),
});

export default connect(mapState, null)(LinkViewPort);
